import { ThemeProvider } from '@mui/material/styles'
import type { ComponentProps, ComponentType, ReactNode } from 'react'

import { theme } from '.'
import { readColorMode } from './colorMode'

/**
 * Two props MUI forwards at runtime and does not declare, so the cast is confined here and repeated
 * nowhere - a cast at call sites is a cast nobody re-checks when the types catch up.
 *
 * `storageManager={null}` is what makes the cookie the only source. `useCurrentColorScheme` calls
 * `storageManager?.({...})`, so with `null` there is no storage and `initialMode` is `defaultMode`
 * unconditionally. Without it MUI installs its own `localStorage` manager and reads `mui-mode` from THIS
 * origin, which then wins over `defaultMode` for good: a stale value left here by an earlier visit would
 * outrank the member's current choice forever, and the failure is one-way and silent - they switch the
 * application to light and keep meeting a dark sign-in screen. Nothing writes that key today, but "no
 * caller exists" is a property of today's code; this is a property of the wiring.
 *
 * `noSsr` exists in the runtime and not in the types. `useCurrentColorScheme` initialises its client flag
 * as `useState(noSsr || !isMultiSchemes)` and only sets it in an effect, which runs after paint. With a
 * single scheme `!isMultiSchemes` is true and nothing flashes; declaring a second scheme flips that, so
 * without `noSsr` the first render returns `colorScheme: undefined`, `theme.palette` falls back to light,
 * and React paints one frame of light-theme colours over the dark canvas the pre-paint sheet has already
 * set.
 *
 * Never pass `forceThemeRerender`: the version that has it freezes `theme.palette` while the CSS variables
 * keep changing - half the screen following the mode and half not. `verify:color-mode` fails if such a
 * version resolves.
 */
type ProviderProps = ComponentProps<typeof ThemeProvider> & {
  noSsr?: boolean
  storageManager?: null
}
const Provider = ThemeProvider as ComponentType<ProviderProps>

/**
 * Read once, at mount, rather than watched.
 *
 * There is no control on these screens to change the mode - no product puts a theme switch on a sign-in
 * page - so the only way the value moves while this document is open is another tab writing it, and the
 * member is by definition not in this one. Re-reading would buy a case nobody reaches at the cost of a
 * subscription to a store that fires no events.
 *
 * The same read runs in the blocking script in `index.html`, which is what sets the attribute before the
 * first paint. This one is what makes MUI's own state agree with that attribute: the script paints the
 * canvas and this decides what the components render.
 */
export default function ColorSchemeProvider({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Provider theme={theme} defaultMode={readColorMode()} storageManager={null} noSsr>
      {children}
    </Provider>
  )
}
